import type { Transaction } from './transaction';

export interface DailyCashFlow {
  date: Date;
  recebimentos: number; // Receitas/Entradas
  pagamentos: number; // Despesas/Saídas  
  transferencias_entrada: number; // Transferências recebidas
  transferencias_saida: number; // Transferências enviadas
  saldo_inicial: number; // Saldo do dia anterior
  saldo_final: number; // Saldo acumulado
  saldo_projetado?: number; // Projeção futura (opcional)
}

export interface CashFlowSummary {
  periodo: {
    inicio: Date;
    fim: Date;
  };
  totais: {
    recebimentos: number;
    pagamentos: number;
    transferencias_entrada: number;
    transferencias_saida: number;
    saldo_liquido: number;
  };
  saldo_inicial: number;
  saldo_final: number;
  dias_com_dados: number;
}

export interface CashFlowFilters {
  periodo: {
    inicio: Date;
    fim: Date;
  };
  visualizacao: 'diario' | 'semanal' | 'mensal';
  incluir_projecoes: boolean;
  contas_selecionadas?: string[]; // IDs das contas de pagamento
  categorias_selecionadas?: string[]; // IDs das categorias
}

export interface CashFlowProjection {
  date: Date;
  tipo: 'receita' | 'despesa' | 'transferencia';
  descricao: string;
  valor: number;
  categoria?: string;
  conta?: string;
  recorrente: boolean;
  confianca: number; // 0-100% de confiança na projeção
}

export interface CashFlowChart {
  labels: string[]; // Datas formatadas
  datasets: {
    recebimentos: number[];
    pagamentos: number[];
    transferencias_entrada: number[];
    transferencias_saida: number[];
    saldo_acumulado: number[];
    saldo_projetado?: number[];
  };
}

export interface CashFlowAnalytics {
  maior_entrada: {
    data: Date;
    valor: number;
    descricao: string;
  };
  maior_saida: {
    data: Date;
    valor: number;
    descricao: string;
  };
  dia_melhor_saldo: {
    data: Date;
    valor: number;
  };
  dia_pior_saldo: {
    data: Date;
    valor: number;
  };
  media_diaria: {
    recebimentos: number;
    pagamentos: number;
    saldo: number;
  };
  tendencia: 'positiva' | 'negativa' | 'estavel';
  volatilidade: number; // Desvio padrão dos saldos diários
}

// Tipos para configurações de visualização
export type CashFlowViewMode = 'chart' | 'table' | 'both';
export type CashFlowPeriod = 'today' | 'week' | 'month' | 'quarter' | 'year' | 'custom';

export interface CashFlowConfig {
  view_mode: CashFlowViewMode;
  default_period: CashFlowPeriod;
  show_projections: boolean;
  show_trends: boolean;
  chart_height: number;
  currency_format: 'BRL' | 'USD' | 'EUR';
  date_format: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
}

// Enums para melhor type safety
export enum CashFlowType {
  RECEBIMENTO = 'recebimento',
  PAGAMENTO = 'pagamento',
  TRANSFERENCIA_ENTRADA = 'transferencia_entrada',
  TRANSFERENCIA_SAIDA = 'transferencia_saida'
}

export enum CashFlowStatus {
  REALIZADO = 'realizado',
  PROJETADO = 'projetado',
  ATRASADO = 'atrasado',
  CANCELADO = 'cancelado'
}

// Tipos para API responses
export interface CashFlowApiResponse {
  success: boolean;
  data: DailyCashFlow[];
  summary: CashFlowSummary;
  analytics: CashFlowAnalytics;
  projections?: CashFlowProjection[];
  errors?: string[];
}

// Tipos para componentes
export interface CashFlowChartProps {
  data: DailyCashFlow[];
  height?: number;
  showProjections?: boolean;
  onDateClick?: (date: Date) => void;
  loading?: boolean;
}

export interface CashFlowTableProps {
  data: DailyCashFlow[];
  onRowClick?: (cashFlow: DailyCashFlow) => void;
  sortable?: boolean;
  filterable?: boolean;
  exportable?: boolean;
  loading?: boolean;
}

export interface CashFlowFiltersProps {
  filters: CashFlowFilters;
  onFiltersChange: (filters: CashFlowFilters) => void;
  availableAccounts?: Array<{ id: string; name: string }>;
  availableCategories?: Array<{ id: string; name: string }>;
}

// Tipos para cálculos e processamento
export interface CashFlowCalculation {
  calcularSaldoDiario: (transacoes: Transaction[], data: Date) => DailyCashFlow;
  calcularTendencia: (fluxos: DailyCashFlow[]) => 'positiva' | 'negativa' | 'estavel';
  calcularVolatilidade: (fluxos: DailyCashFlow[]) => number;
  projetarFluxo: (historico: DailyCashFlow[], dias: number) => CashFlowProjection[];
}

// Tipos para exportação
export interface CashFlowExportData {
  formato: 'xlsx' | 'csv' | 'pdf';
  dados: DailyCashFlow[];
  resumo: CashFlowSummary;
  periodo: string;
  filtros_aplicados: string[];
}

// Constantes úteis
export const CASH_FLOW_COLORS = {
  recebimentos: '#10B981', // green-500
  pagamentos: '#EF4444', // red-500
  transferencias_entrada: '#3B82F6', // blue-500
  transferencias_saida: '#F59E0B', // amber-500
  saldo: '#6366F1', // indigo-500
  projecao: '#8B5CF6' // violet-500
} as const;

export const CASH_FLOW_LABELS = {
  recebimentos: 'Recebimentos',
  pagamentos: 'Pagamentos',
  transferencias_entrada: 'Transferências (entrada)',
  transferencias_saida: 'Transferências (saída)',
  saldo: 'Saldo',
  saldo_inicial: 'Saldo Inicial',
  saldo_final: 'Saldo Final'
} as const;